import { EngineReportDetails, LubeOilSystemVm } from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { RangeLimits, TooltipSeverity, getHardValidation, getRangeTooltip, isOutOfRange } from '../range-tooltip.utils';

const TEMPERATURE_LIMITS = { min: 0, max: 100 } as const;

const MIN_PRESSURE = 0;

const SOFT_RANGE_LIMITS = {
  loTempTcBlowerInlet: { min: 15, max: 65 },
} as const;

const SPECIFIC_CYL_LO_CONSUMPTION_LIMITS = { min: 0 } as const;

function pressureLimits(max: number | undefined): RangeLimits {
  return max != null && max > 0 ? { min: MIN_PRESSURE, max } : { min: MIN_PRESSURE };
}

export function buildLubeOilSystemVm(
  details: EngineReportDetails | undefined,
  vesselSpec: VesselSpec | undefined
): LubeOilSystemVm {
  const engine = details?.mainEngine != null ? vesselSpec?.engine?.me_engines?.[details.mainEngine] : undefined;

  const engineInlet = getHardValidation(
    details?.loTempEngineInlet,
    TEMPERATURE_LIMITS,
    'LO Temperature Engine Inlet',
    Units.degreesCelsius,
    true
  );
  const thrustSegment = getHardValidation(
    details?.loTempThrustSegment,
    TEMPERATURE_LIMITS,
    'LO Temperature Thrust Segment',
    Units.degreesCelsius
  );
  const blowerInlet = getHardValidation(
    details?.loTempTcBlowerInlet,
    TEMPERATURE_LIMITS,
    'LO Temperature T/C Blower Inlet',
    Units.degreesCelsius
  );
  const turbineOutlet = getHardValidation(
    details?.loTempTcTurbineOutlet,
    TEMPERATURE_LIMITS,
    'LO Temperature T/C Turbine Outlet',
    Units.degreesCelsius
  );
  const systemOil = getHardValidation(
    details?.loPressSystemOil,
    pressureLimits(engine?.max_lo_press_system_oil),
    'LO Pressure System Oil',
    Units.bar,
    true
  );
  const turbineOil = getHardValidation(
    details?.loPressTurbineOil,
    pressureLimits(engine?.max_lo_press_turbine_oil),
    'LO Pressure Turbine Oil',
    Units.bar
  );
  const coolingOil = getHardValidation(
    details?.loPressCoolingOil,
    pressureLimits(engine?.max_lo_press_cooling_oil),
    'LO Pressure Cooling',
    Units.bar
  );
  const specificCylLoConsumption = getHardValidation(
    details?.specificCylLoConsumption,
    SPECIFIC_CYL_LO_CONSUMPTION_LIMITS,
    'Specific Cylinder LO Consumption',
    Units.gramsPerKilowattHour
  );

  const loTempTcBlowerInletWarning =
    !blowerInlet.invalid && isOutOfRange(details?.loTempTcBlowerInlet, SOFT_RANGE_LIMITS.loTempTcBlowerInlet);
  const loTempTcBlowerInletWarningTooltip = getRangeTooltip(
    'LO Temperature T/C Blower Inlet',
    SOFT_RANGE_LIMITS.loTempTcBlowerInlet,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  return {
    loTempEngineInletInvalid: engineInlet.invalid,
    loTempEngineInletInvalidTooltip: engineInlet.tooltip,
    loTempThrustSegmentInvalid: thrustSegment.invalid,
    loTempThrustSegmentInvalidTooltip: thrustSegment.tooltip,
    loTempTcBlowerInletInvalid: blowerInlet.invalid,
    loTempTcBlowerInletInvalidTooltip: blowerInlet.tooltip,
    loTempTcBlowerInletWarning,
    loTempTcBlowerInletWarningTooltip,
    loTempTcTurbineOutletInvalid: turbineOutlet.invalid,
    loTempTcTurbineOutletInvalidTooltip: turbineOutlet.tooltip,
    loPressSystemOilInvalid: systemOil.invalid,
    loPressSystemOilInvalidTooltip: systemOil.tooltip,
    loPressTurbineOilInvalid: turbineOil.invalid,
    loPressTurbineOilInvalidTooltip: turbineOil.tooltip,
    loPressCoolingOilInvalid: coolingOil.invalid,
    loPressCoolingOilInvalidTooltip: coolingOil.tooltip,
    specificCylLoConsumptionInvalid: specificCylLoConsumption.invalid,
    specificCylLoConsumptionInvalidTooltip: specificCylLoConsumption.tooltip,
  };
}
